// sandboxComponents.js — the house components a sandboxed AI-written app may
// use (ISOLATED-RUNTIME-PLAN §9.3). The opaque frame has no shell registry: the
// runtime's default-config built-ins render there, house components only if
// they travel in the boot payload. A shell definition is a closure in the shell
// bundle; a stringified copy that reads module scope, or el.calls a shell
// function the frame lacks, breaks in the frame. So the set is DATA, and
// __tests__/sandboxComponents.test.js PROVES every entry in the real runtime
// (__tests__/sandboxComponentsProver.js): no el.call outside the frame
// registry, no element method the frame lacks, no free name outside the
// frame's globals (a closure over shell scope), and a render with its own
// default state through the real bootstrap, storage throwing, with no error. Every allowlisted house
// component is classified: listed here, or dropped with its reason.
// The app-rules component allowlist stays the gate on what a page may NAME.

import { deepStringifyFunctions } from '@symbo.ls/utils'

export const SANDBOX_COMPONENTS = Object.freeze([
  'AgentAvatar',
  'AiActionRow',
  'AiMenuHeading',
  'AiMenuPanel',
  'AiOptionRow',
  'AppCapsuleBtn',
  'AppCapsuleBtnDanger',
  'AppCapsuleBtnGhost',
  'AppCapsuleBtnPrimary',
  'AppCapsuleBtnToolbar',
  'AppChoiceChip',
  'AppChoiceChips',
  'AppColorChip',
  'AppColorChips',
  'AppDialogBody',
  'AppDialogFoot',
  'AppDialogHint',
  'AppDialogTitle',
  'AppField',
  'AppFieldChips',
  'AppFieldColors',
  'AppFieldError',
  'AppFieldHint',
  'AppFieldLabel',
  'AppKeycap',
  'AppLabelStack',
  'AppMain',
  'AppMicroLabel',
  'AppNavLink',
  'AppOption',
  'AppOrgSwitchOverlay',
  'AppPage',
  'AppPageEyebrow',
  'AppPaginationNext',
  'AppPaginationPrevious',
  'AppPaginationStep',
  'AppScrollRow',
  'AppSidebarGroup',
  'AppStatusChip',
  'AppTable',
  'AppTableHead',
  'AppTableNum',
  'AppTableRow',
  'AppToolbar',
  'AssetsFromInsert',
  'BoxModelAside',
  'BoxSizeItem',
  'CanvasDots',
  'CanvasMobileSidebarPanelChrome',
  'CanvasSidebarCard',
  'CanvasSidebarIsland',
  'ChatConfidenceFactor',
  'ChatMarkdown',
  'ChromeExt',
  'CommonCard',
  'CompactIconButton',
  'DesignSystem',
  'DesignSystemBanners',
  'DraftSwitcher',
  'DragComponentsTutorial',
  'DropdownButton',
  'EmptyState',
  'EmptyStateImages',
  'EmptyStateLoading',
  'EmptyStateTiles',
  'ErrorState',
  'Feedback',
  'FontsFromInsert',
  'GeexBlobPercent',
  'GeexGridBars',
  'GeexRankedStack',
  'GeexSegmentedBar',
  'GeexStackedColumn',
  'GeexTonalBars',
  'GeexValueRules',
  'GlyphPlateHolder',
  'GridChooser',
  'IconGridTypes',
  'IconNavbar',
  'InlineSelect',
  'InsertSectionShadow',
  'InstallFooter',
  'InstallHero',
  'InstallableRailSkeleton',
  'InstallableStateBadge',
  'IntegrationItem',
  'IntegrationScopeBadge',
  'IntegrationScopeOption',
  'IntegrationsList',
  'InviteForm',
  'LanguageOption',
  'LibraryItemInLearn',
  'LoadingSpinner',
  'LoginForm',
  'MemberProfileCheckItem',
  'MicroNavbar',
  'ModalFooter',
  'NavigationDots',
  'OrderingItem',
  'Page',
  'PageBar',
  'PageBarBtn',
  'PageBarMeta',
  'PageBarTitle',
  'PageTemplate',
  'PeopleSection',
  'ProfileGrantCard',
  'ProgressBar',
  'RequestAccessButtons',
  'ResetEmailForm',
  'ResetPasswordsForm',
  'SelectRoleField',
  'ServerPreview',
  'SignupForm',
  'Skeleton',
  'SkeletonCard',
  'SkeletonCircle',
  'SkeletonRow',
  'Tests',
  'ToolTerminal',
  'UploadProgress',
  'UserProfileIntegrationRow',
  'WaitlistForm'
])

export const SANDBOX_COMPONENTS_DROPPED = Object.freeze({
  AppChoiceGroup: 'calls element methods the frame lacks: el.options',
  AppSidebarRow: 'calls element methods the frame lacks: el.onClick',
  CanvasPanel: 'calls element methods the frame lacks: el.getUserSettings',
  DragHandler: 'calls element methods the frame lacks: el.getUserSettings',
  DragParagraph:
    'render error: [DOMQL] Lifecycle "onRender" error: Error: Could not import the module \'lottie-web\'.',
  ItemPreview: 'calls element methods the frame lacks: el.getAppKey',
  Lottie: 'el.call outside the frame: exec, isString, replaceLiteralsWithObjectFields',
  ProjectInfo:
    "render error: [smbls] createChildren failed at smblsapp.ProjectInfo: el.getAppKey is not a function. (In 'el.getAppKey()', 'el.getAppKey' is undefined) ",
  ResizeOverlay: 'calls element methods the frame lacks: el.getUserSettings',
  ResponsiveOverlay: 'calls element methods the frame lacks: el.isEmbed',
  SettingsTabs: 'calls element methods the frame lacks: el.isAdmin, el.isOwner, el.isEditMode',
  SwitchInput: 'calls element methods the frame lacks: el.onCheck',
  UsersTableCopy: 'calls element methods the frame lacks: el.getRootProjectsUsers'
})

// The payload budget (measured 2026-10-02: 86.4 KB for the 123).
export const SANDBOX_COMPONENTS_MAX_BYTES = 100000

const cache = new WeakMap()

// The listed definitions out of a registry (ctx.components), stringified once
// per registry object (functions as text; the frame revives them with
// Smbls.deepDestringifyFunctions). A name the registry lacks is skipped.
export const sandboxComponentPayload = (components) => {
  if (!components || typeof components !== 'object') return {}
  if (cache.has(components)) return cache.get(components)
  const out = {}
  for (const k of SANDBOX_COMPONENTS) {
    const def = components[k]
    if (!def || typeof def !== 'object') continue
    out[k] = JSON.parse(JSON.stringify(deepStringifyFunctions(def, Array.isArray(def) ? [] : {})))
  }
  cache.set(components, out)
  return out
}
